import {
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { type User, useAdminUsers } from "../../hooks/api/useUsers";

interface UserSelectComboboxProps {
  /** 現在選択中の代行対象ユーザー（null = 代行なし＝自分名義） */
  proxyTarget: User | null;
  /** 選択変更ハンドラ。null で「自分（代行解除）」を意味する */
  onChange: (user: User | null) => void;
  /** 検索対象から除外するメールアドレス（通常は自分自身） */
  excludeEmail?: string;
  /** ラベルテキスト */
  label?: string;
  /** disabled 状態 */
  disabled?: boolean;
}

const SEARCH_DEBOUNCE_MS = 200;
const MAX_RESULTS = 20;

/**
 * 管理者向けユーザー検索コンボボックス。
 * - WAI-ARIA combobox + listbox パターンを実装（aria-activedescendant 方式）
 * - キーボード操作 (ArrowUp/Down, Enter, Escape) 対応
 * - 検索クエリは debounce してから API に送信
 */
export function UserSelectCombobox({
  proxyTarget,
  onChange,
  excludeEmail,
  label = "代行対象ユーザー",
  disabled = false,
}: UserSelectComboboxProps) {
  const [inputValue, setInputValue] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const inputId = useId();
  const listboxId = useId();
  const helperId = useId();

  // debounce 処理
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(inputValue.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const shouldQuery = debouncedQuery.length > 0;
  const { data, isLoading, isError } = useAdminUsers(
    shouldQuery ? debouncedQuery : undefined
  );

  const allResults = data?.users ?? [];
  const filteredResults = useMemo(
    () =>
      allResults
        .filter((u) => !excludeEmail || u.email !== excludeEmail)
        .slice(0, MAX_RESULTS),
    [allResults, excludeEmail]
  );

  const handleSelect = useCallback(
    (user: User) => {
      onChange(user);
      setInputValue("");
      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange(null);
    setInputValue("");
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  }, [onChange]);

  const moveHighlight = useCallback(
    (delta: 1 | -1) => {
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      setHighlightedIndex((prev) => {
        if (filteredResults.length === 0) {
          return -1;
        }
        const next = prev + delta;
        if (next >= filteredResults.length) {
          return 0;
        }
        if (next < 0) {
          return filteredResults.length - 1;
        }
        return next;
      });
    },
    [isOpen, filteredResults.length]
  );

  const setHighlightToEdge = useCallback(
    (position: "first" | "last") => {
      if (filteredResults.length === 0) {
        return;
      }
      if (!isOpen) {
        setIsOpen(true);
      }
      setHighlightedIndex(
        position === "first" ? 0 : filteredResults.length - 1
      );
    },
    [isOpen, filteredResults.length]
  );

  const commitHighlighted = useCallback(() => {
    if (!isOpen || highlightedIndex < 0) {
      return;
    }
    const user = filteredResults[highlightedIndex];
    if (user) {
      handleSelect(user);
    }
  }, [isOpen, highlightedIndex, filteredResults, handleSelect]);

  const closeList = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  // 矢印キー: 閉じた状態でも moveHighlight 内で開く処理が走る
  const handleArrowKey = useCallback(
    (e: KeyboardEvent<HTMLInputElement>): boolean => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        moveHighlight(1);
        return true;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        moveHighlight(-1);
        return true;
      }
      return false;
    },
    [moveHighlight]
  );

  // Home/End キー: リスト先頭・末尾へジャンプ
  const handleEdgeKey = useCallback(
    (e: KeyboardEvent<HTMLInputElement>): boolean => {
      if (e.key === "Home") {
        e.preventDefault();
        setHighlightToEdge("first");
        return true;
      }
      if (e.key === "End") {
        e.preventDefault();
        setHighlightToEdge("last");
        return true;
      }
      return false;
    },
    [setHighlightToEdge]
  );

  // Enter/Escape: 確定 or キャンセル
  const handleControlKey = useCallback(
    (e: KeyboardEvent<HTMLInputElement>): boolean => {
      if (e.key === "Enter" && highlightedIndex >= 0) {
        e.preventDefault();
        commitHighlighted();
        return true;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closeList();
        return true;
      }
      return false;
    },
    [highlightedIndex, commitHighlighted, closeList]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (disabled) {
        return;
      }
      if (handleArrowKey(e)) {
        return;
      }
      // 以降のキーはリストが開いている時のみ有効
      if (!isOpen) {
        return;
      }
      if (handleEdgeKey(e)) {
        return;
      }
      handleControlKey(e);
    },
    [disabled, isOpen, handleArrowKey, handleEdgeKey, handleControlKey]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  // クリック外でリストを閉じる
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        inputRef.current?.contains(target) ||
        listboxRef.current?.contains(target)
      ) {
        return;
      }
      closeList();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, closeList]);

  const showResults =
    isOpen && shouldQuery && !isLoading && filteredResults.length > 0;
  const showEmptyMessage =
    isOpen && shouldQuery && !isLoading && filteredResults.length === 0;
  const showLoading = isOpen && shouldQuery && isLoading;
  const showError = isOpen && shouldQuery && isError;

  if (proxyTarget) {
    return (
      <SelectedUserBadge
        user={proxyTarget}
        labelText={label}
        labelId={`${inputId}-label`}
        disabled={disabled}
        onClear={handleClear}
      />
    );
  }

  return (
    <div className="w-full">
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label}
      </label>

      <div className="relative">
        <ComboboxInput
          ref={inputRef}
          id={inputId}
          listboxId={listboxId}
          helperId={helperId}
          value={inputValue}
          isOpen={isOpen}
          activeOptionId={
            highlightedIndex >= 0
              ? `${listboxId}-option-${highlightedIndex}`
              : undefined
          }
          disabled={disabled}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
        />

        <p id={helperId} className="mt-1 text-xs text-gray-500">
          自分以外を予約する場合のみ選択してください
        </p>

        {showResults && (
          <ResultListbox
            ref={listboxRef}
            id={listboxId}
            users={filteredResults}
            highlightedIndex={highlightedIndex}
            onSelect={handleSelect}
            onHighlight={setHighlightedIndex}
          />
        )}

        {showEmptyMessage && (
          <PopoverMessage variant="info" role="status">
            該当するユーザーが見つかりません
          </PopoverMessage>
        )}
        {showLoading && (
          <PopoverMessage variant="info" role="status">
            検索中...
          </PopoverMessage>
        )}
        {showError && (
          <PopoverMessage variant="error" role="alert">
            ユーザー検索に失敗しました
          </PopoverMessage>
        )}
      </div>
    </div>
  );
}

interface SelectedUserBadgeProps {
  user: User;
  labelText: string;
  labelId: string;
  disabled: boolean;
  onClear: () => void;
}

function SelectedUserBadge({
  user,
  labelText,
  labelId,
  disabled,
  onClear,
}: SelectedUserBadgeProps) {
  // <label htmlFor> は labelable な要素 (input, button 等) しか紐付けできない。
  // バッジは div なので role="group" + aria-labelledby パターンで命名する。
  return (
    <div className="w-full">
      <div
        id={labelId}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {labelText}
      </div>
      <div
        role="group"
        aria-labelledby={labelId}
        className="flex items-center justify-between gap-2 rounded-lg border border-primary-300 bg-primary-50 px-3 py-2"
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-primary-900">
            {user.name}
          </p>
          <p className="truncate text-xs text-primary-700">{user.email}</p>
        </div>
        <button
          type="button"
          onClick={onClear}
          disabled={disabled}
          className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="代行対象ユーザーの選択を解除"
        >
          解除
        </button>
      </div>
    </div>
  );
}

interface ComboboxInputProps {
  id: string;
  listboxId: string;
  helperId: string;
  value: string;
  isOpen: boolean;
  activeOptionId: string | undefined;
  disabled: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
}

const ComboboxInput = forwardRef<HTMLInputElement, ComboboxInputProps>(
  (
    {
      id,
      listboxId,
      helperId,
      value,
      isOpen,
      activeOptionId,
      disabled,
      onChange,
      onFocus,
      onKeyDown,
    },
    ref
  ) => (
    <input
      ref={ref}
      id={id}
      type="text"
      role="combobox"
      aria-expanded={isOpen}
      aria-controls={listboxId}
      aria-autocomplete="list"
      aria-activedescendant={activeOptionId}
      aria-describedby={helperId}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      disabled={disabled}
      placeholder="名前またはメールアドレスで検索"
      autoComplete="off"
      className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50"
    />
  )
);
ComboboxInput.displayName = "ComboboxInput";

interface ResultListboxProps {
  id: string;
  users: User[];
  highlightedIndex: number;
  onSelect: (user: User) => void;
  onHighlight: (index: number) => void;
}

const ResultListbox = forwardRef<HTMLDivElement, ResultListboxProps>(
  ({ id, users, highlightedIndex, onSelect, onHighlight }, ref) => (
    <div
      ref={ref}
      id={id}
      role="listbox"
      aria-label="ユーザー候補"
      tabIndex={-1}
      className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg focus:outline-none"
    >
      {users.map((user, index) => (
        <ComboboxOption
          key={user.email}
          id={`${id}-option-${index}`}
          user={user}
          isHighlighted={index === highlightedIndex}
          onSelect={onSelect}
          onHover={() => onHighlight(index)}
        />
      ))}
    </div>
  )
);
ResultListbox.displayName = "ResultListbox";

interface ComboboxOptionProps {
  id: string;
  user: User;
  isHighlighted: boolean;
  onSelect: (user: User) => void;
  onHover: () => void;
}

function ComboboxOption({
  id,
  user,
  isHighlighted,
  onSelect,
  onHover,
}: ComboboxOptionProps) {
  const handleMouseDown = (e: ReactMouseEvent<HTMLDivElement>) => {
    // input の blur を防ぐため preventDefault
    e.preventDefault();
    onSelect(user);
  };
  return (
    // role="option" は WAI-ARIA 仕様上 aria-selected が必須属性。
    // aria-activedescendant 方式の combobox では、ハイライト中の 1 件のみ
    // aria-selected="true" となり、他は false（デフォルト）となる。
    <div
      id={id}
      role="option"
      aria-selected={isHighlighted}
      tabIndex={-1}
      onMouseDown={handleMouseDown}
      onMouseEnter={onHover}
      className={`cursor-pointer px-3 py-2 text-sm ${
        isHighlighted
          ? "bg-primary-50 text-primary-900"
          : "text-gray-900 hover:bg-gray-50"
      }`}
    >
      <p className="truncate font-medium">{user.name}</p>
      <p className="truncate text-xs text-gray-500">{user.email}</p>
    </div>
  );
}

interface PopoverMessageProps {
  variant: "info" | "error";
  role: "status" | "alert";
  children: React.ReactNode;
}

function PopoverMessage({ variant, role, children }: PopoverMessageProps) {
  const className =
    variant === "error"
      ? "absolute z-10 mt-1 w-full rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 shadow-lg"
      : "absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-500 shadow-lg";
  return (
    <div className={className} role={role}>
      {children}
    </div>
  );
}
